import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { MediaPickerButton } from "../media/MediaPickerButton";
import { Loader2, Store, MapPin, Settings2, Save } from "lucide-react";
import { StoreSetting } from "@/types/setting-type";
import { useStoreSettingForm } from "@/hooks/use-store-setting";

const TOGGLE_ITEMS: { id: keyof StoreSetting; title: string; desc: string }[] = [
  {
    id: "autoDetectProductCode",
    title: "Auto-detect codes",
    desc: "Scan codes in customer chats",
  },
  {
    id: "showOutOfStock",
    title: "Show out-of-stock",
    desc: "Keep visible even when empty",
  },
  {
    id: "autoConfirmOrder",
    title: "Auto-confirm orders",
    desc: "Approve on payment receipt",
  },
  {
    id: "isEnabledSendKhqr",
    title: "Send KHQR",
    desc: "Automatically send KHQR payment QR to customers",
  },
];

export default function StoreSettingForm() {
  const { settings, isSaving, updateField, saveSettings } = useStoreSettingForm();

  return (
    <div className="mx-auto space-y-6">
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5 text-blue-600" />
            <CardTitle>Store Settings</CardTitle>
          </div>
          <CardDescription>
            Manage your public store identity and contact information.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* --- Section 1: Identity & Logo --- */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Logo */}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-sm font-semibold">Store Logo</Label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 p-4 border rounded-xl bg-slate-50/50">
                <div className="relative w-20 h-20 bg-white border rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                  {settings.logo ? (
                    <img src={settings.logo} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Store className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                <div className="space-y-2 flex-1 w-full">
                  <div className="flex gap-2">
                    <Input
                      className="bg-white"
                      placeholder="Logo URL"
                      value={settings.logo || ""}
                      onChange={(e) => updateField("logo", e.target.value)}
                    />
                    <MediaPickerButton
                      label="Browse"
                      multi={false}
                      allowUpload
                      previewUrl={settings.logo || undefined}
                      onClear={() => updateField("logo", "")}
                      onPick={(items) =>
                        updateField("logo", items[0]?.s3Url || items[0]?.fileUrl || "")
                      }
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 italic">
                    Recommended: Square PNG or JPG, max 2MB.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Store Name</Label>
              <Input
                placeholder="E.g. My Awesome Shop"
                value={settings.name || ""}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="contact@store.com"
                value={settings.email || ""}
                onChange={(e) => updateField("email", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                placeholder="+855 12 345 678"
                value={settings.phone || ""}
                onChange={(e) => updateField("phone", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Website URL</Label>
              <Input
                placeholder="https://store.com"
                value={settings.website || ""}
                onChange={(e) => updateField("website", e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* --- Section 2: Physical Address --- */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Business Location
              </h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Address Line 1</Label>
                <Input
                  placeholder="Street, Building No."
                  value={settings.addressLine1 || ""}
                  onChange={(e) => updateField("addressLine1", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Address Line 2</Label>
                <Input
                  placeholder="City, District, Province"
                  value={settings.addressLine2 || ""}
                  onChange={(e) => updateField("addressLine2", e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* --- Section 3: System Preferences --- */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Inventory & Automation
              </h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {TOGGLE_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 border rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="space-y-0.5">
                    <Label className="text-base cursor-pointer" htmlFor={item.id}>
                      {item.title}
                    </Label>
                    <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                  <Switch
                    id={item.id}
                    checked={!!settings[item.id]}
                    onCheckedChange={(val) => updateField(item.id, val)}
                  />
                </div>
              ))}

              {/* Order Footnote */}
              <div className="flex flex-col gap-3 p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                <div className="flex flex-col gap-1">
                  <Label className="text-base cursor-pointer" htmlFor="orderFootNote">
                    Order Footnote
                  </Label>
                  <p className="text-xs text-slate-500">
                    Appears at the bottom of order summaries.
                  </p>
                </div>
                <Textarea
                  id="orderFootNote"
                  placeholder={`Thank you for your purchase!\nPlease visit us again.`}
                  value={settings.orderFootNote || ""}
                  onChange={(e) => updateField("orderFootNote", e.target.value)}
                  className="min-h-[120px] bg-white rounded-xl border-slate-200 resize-none text-base leading-relaxed"
                />
              </div>
            </div>
          </div>
        </CardContent>

        <div className="p-6 border-t bg-slate-50/50 flex justify-end">
          <Button
            disabled={isSaving}
            size="lg"
            onClick={saveSettings}
            className="px-8 font-semibold shadow-md active:scale-95 transition-all"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save All Changes
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
